<template>
  <div class="common-card">
    <modal-receipt-filter
      :filter_invoice_id.sync="filter_invoice_id"
      :filter_customer_id.sync="filter_customer_id"
      :filter_project_id.sync="filter_project_id"
      :filter_payment_method_id.sync="filter_payment_method_id"
      :filter_start_date.sync="filter_start_date"
      :filter_end_date.sync="filter_end_date"
      :filter_min_amount.sync="filter_min_amount"
      :filter_max_amount.sync="filter_max_amount"

      :invoiceOptions="handleAdminInvoiceOptions"
      :customerOptions="customerOptions"
      :paymentMethodOptions="paymentMethodOptions"
      :projectOptions="projectOptions"
      @refetch-data="refetchData"
    />
    <b-card class="top mb-0">
      <div class="d-flex flex-wrap align-items-center justify-content-between">
        <div class="d-flex mb-1 mb-sm-0 align-items-center">
          <h4 class="font-weight-bolder ml-1 text-nowrap mb-0">
            <font-awesome-icon
              class="mr-50"
              icon="fa-solid fa-file-invoice-dollar"
            />
            收據資料
          </h4>
          <div class="d-flex ml-3 align-items-center">
            <label>顯示</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>條目</label>
          </div>
        </div>
        <div class="d-flex flex-wrap mobile-w100 mt-1 mt-lg-0">
          <div class="d-flex align-items-center mr-2 mb-1 mb-sm-0">
            <input
              id="is_open_in_new_window"
              v-model="openInNewWindow"
              type="checkbox"
              class="mr-25"
            />
            <label for="is_open_in_new_window">在新視窗編輯</label>
          </div>
          <b-form-input
            v-model="searchQuery"
            class="search-form mb-1 mb-sm-0 mr-sm-1"
            placeholder="搜尋收據"
          />
          <b-button
            variant="primary"
            class="mobile-w100 mb-1 mb-sm-0 mr-sm-1"
            @click="$bvModal.show('modal-receipt-filter')"
            ><feather-icon
              class="mr-1"
              icon="filterIcon"
              size="18"
            ></feather-icon
            >篩選</b-button
          >
          <b-button class="mobile-w100" :to="{ name: 'receipt-create' }"
          v-if="ability.can('create', 'receipts')"
          >
            <feather-icon class="mr-1" icon="plusIcon" size="18"></feather-icon
            >新增收據</b-button
          >
        </div>
      </div>
    </b-card>

    <b-card no-body class="card-statistics bottom">
      <b-card-body class="p-2">
        <b-table
          ref="refReceiptListTable"
          class="position-relative"
          :items="fetchReceipts"
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty
          empty-text="未找到匹配的記錄"
          :sort-desc.sync="isSortDirDesc"
        >
          <template #cell(payment_method)="data">
            {{ getPaymentMethod(data.item.payment_method_id) }}
          </template>


          <template #cell(id)="data">
              {{ data.item.receipt_number }}
          </template>

          <!-- Column: amount 金額格式化 - 基於收據項目總和計算 -->
          <template #cell(amount)="data">
            ${{ calculateReceiptTotalAmount(data.item).toFixed(2) }}
          </template>

          <!-- Column: total_refund 已退款金額格式化 -->
          <template #cell(total_refund)="data">
            ${{ Number(data.item.total_refund || 0).toFixed(2) }}
          </template>

          <!-- Column: show_details receipt_items -->
          <template #cell(show_details)="row">
            <b-button
              v-if="row.item.receipt_items"
              size="sm"
              @click="row.toggleDetails"
              class="mr-2"
            >
              {{ row.detailsShowing ? "隱藏" : "顯示" }}
            </b-button>
          </template>
          <template #row-details="row">
            <b-row
              class="p-2 backgroundColor5 mb-1"
              v-for="(receipt, index) in row.item.receipt_items"
              :key="`receipt${index}`"
            >
              <b-col cols="12" md="2" class="mb-50">
                <span class="">發票編號：</span>
                <span class="">
                  <a
                    v-if="ability.can('update', 'invoices') && receipt.invoice && receipt.invoice.invoice_number && receipt.invoice.id"
                    :href="$router.resolve({ name: 'invoice-edit', params: { id: receipt.invoice.id } }).href"
                    target="_blank"
                    class="text-primary"
                  >
                    {{ receipt.invoice.invoice_number }}
                  </a>
                  <span v-else>
                    {{ receipt.invoice && receipt.invoice.invoice_number }}
                  </span>
                </span>
              </b-col>
              <b-col cols="12" md="2" class="mb-50">
                <span class="">付款日期：</span>
                <span class="">{{ receipt.payment_date }}</span>
              </b-col>
              <b-col cols="12" md="2" class="mb-50">
                <span class="">付款方式：</span>
                <span class="">{{ receipt.payment_method && receipt.payment_method.name }}</span>
              </b-col>
              <b-col cols="12" md="2" class="mb-50">
                <span class="">付款金額：</span>
                <div v-if="receipt.is_reprint" class="reprint-amount text-warning">
                  <div class="font-weight-bold">${{ Number(receipt.amount).toFixed(2) }}</div>
                  <small class="text-muted">
                    (原額: ${{ getOriginalReceiptAmount(receipt) }})
                  </small>
                </div>
                <span v-else class="">${{ Number(receipt.amount).toFixed(2) }}</span>
              </b-col>
              <b-col cols="12" md="4" class="mb-50">
                <!-- <span class="">款項摘要：</span> -->
                <span class="" :class="{ 'text-warning': receipt.is_reprint }">
                  {{ receipt.payment_step ? 
                  `${receipt.payment_step.step_phase} ${receipt.payment_step.step_name ? `：
                  ${receipt.payment_step.step_name}` : ''} - 金額：$${receipt.payment_step.step_amount}`
                  : '' }}
                  <span v-if="receipt.is_reprint" class="badge badge-warning ml-1">(Re-Print)</span>
                </span>
              </b-col>
            </b-row>
          </template>

          <!-- Column: stamp -->
          <template #cell(stamp)="data">
            <feather-icon
              v-if="data.item.stamp"
              icon="CheckIcon"
              size="18"
              class="mr-50 text-success"
            />
          </template>

          <template #cell(customer.name_cn)="data">
            <a
              v-if="
                ability.can('view_detail', 'setting_customers') &&
                ((data.item.customer && data.item.customer.id) || data.item.customer_id)
              "
              :href="
                $router.resolve({
                  name: 'customer-view',
                  params: {
                    id:
                      data.item.customer && data.item.customer.id
                        ? data.item.customer.id
                        : data.item.customer_id,
                  },
                }).href
              "
              class="text-primary"
            >
              {{ data.value }}
            </a>
            <span v-else>{{ data.value }}</span>
          </template>

          <template #cell(created_at)="data">
            {{  getDateTime(data.item.created_at) }}
          </template>

          <template #cell(actions)="data">
            <b-dropdown
              :class="`action-trigger-btn ${
                receiptsList.length <= 3 ? 'hor' : 'ver'
              }`"
              :right="$store.state.appConfig.isRTL"
            >
              <template #button-content>
                <feather-icon
                  icon="EyeIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>

              <b-dropdown-item
                v-if="ability.can('print', 'receipts')"
                :to="{ name: 'receipt-preview', params: { id: data.item.id } }"
              >
                <feather-icon icon="FileTextIcon" />
                <span class="align-middle ml-50">列印收據</span>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="ability.can('update', 'receipts')"
                @click="handleEditClick(data.item.id)"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">修改資料</span>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="ability.can('delete', 'receipts')"
                @click="removeItem(data.item.id)"
              >
                <feather-icon icon="Trash2Icon" />
                <span class="align-middle ml-50">刪除記錄</span>
              </b-dropdown-item>
            
            </b-dropdown>
          </template>
        </b-table>
        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted"
                >在{{ dataMeta.of }}個記錄中，正在顯示 {{ dataMeta.from }} 至
                {{ dataMeta.to }}個記錄</span
              >
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRow"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>
    
<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
} from "bootstrap-vue";
import vSelect from "vue-select";
import store from "@/store";
import { ref, onUnmounted, watch } from "@vue/composition-api";
import useReceiptsList from "./useReceiptsList";
import receiptStoreModule from "../receiptStoreModule";
import ModalReceiptFilter from "../modal/ModalReceiptFilter.vue";
import { getUserData } from "@/auth/utils";
import ability from "@/libs/acl/ability";

export default {
  components: {
    ModalReceiptFilter,
    BCard,
    BCardBody,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    vSelect,
  },
  computed: {
    handleAdminInvoiceOptions() {
      const isCustomer = this.userData && this.userData.user_type_id == 2;

      // 員工且具備 receipts.admin -> 顯示所有發票
      if (!isCustomer && this.ability.can("admin", "receipts")) {
        return this.invoiceOptions;
      }

      // 沒有任何收據資料時，無可用發票
      if (!this.receiptsList || !this.receiptsList.length) {
        return [];
      }

      // 從收據項目與收據本身蒐集發票ID
      const itemInvoiceIds = this.receiptsList
        .flatMap(r => (r.receipt_items || []).map(ri => ri.invoice_id))
        .filter(id => id !== null && id !== undefined);

      const receiptInvoiceIds = this.receiptsList
        .map(r => r.invoice_id)
        .filter(id => id !== null && id !== undefined);

      // 客戶：僅使用 receipt_items 內的發票
      // 員工（非 admin）：合併收據本身與項目中的發票
      const effectiveIds = isCustomer ? itemInvoiceIds : [...itemInvoiceIds, ...receiptInvoiceIds];
      const uniqueInvoiceIds = [...new Set(effectiveIds)];

      return this.invoiceOptions.filter(inv => uniqueInvoiceIds.includes(inv.id));
    }
  },  
  data() {
    return {
      userData: getUserData(),
    };
  },
  methods: {
    handleEditClick(id) {
      const editRoute = this.$router.resolve({
        name: 'receipt-edit',
        params: { id: id }
      });

      if (this.openInNewWindow) {
        // 在新視窗中開啟
        window.open(editRoute.href, '_blank');
      } else {
        // 在當前頁面中導航
        this.$router.push({
          name: 'receipt-edit',
          params: { id: id }
        });
      }
    },
    removeItem(id) {
      // check number of record
      this.$swal({
        title: "你確定嗎?",
        text: "您的決定是最終決定，您將無法取回這記錄。",
        showCancelButton: true,
        confirmButtonText: "刪除",
        cancelButtonText: "取消",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.$store
            .dispatch("receipt/deleteReceipt", { id })
            .then((response) => {
              this.$swal({
                text: response.data.message,
                icon: "success",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
              this.refetchData();
            })
            .catch((error) => {
              this.$swal({
                text: error.response.data.message,
                icon: "error",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },  
                buttonsStyling: false,
              });
            });
        }
      });
    },
    getPaymentMethod(methodId) {
      if (!methodId || !this.paymentMethodOptions || !this.paymentMethodOptions.length) {
        return '';
      }
      const method = this.paymentMethodOptions.find(function(ele) {
        return ele.id === methodId;
      });
      return method ? method.name : '';
    },

    // 計算收據的實際總金額（基於收據項目總和）
    calculateReceiptTotalAmount(receipt) {
      if (!receipt.receipt_items || receipt.receipt_items.length === 0) {
        // 如果沒有收據項目，回退到使用 receipt.amount
        return Number(receipt.amount) || 0;
      }

      // 計算所有收據項目的金額總和
      const totalAmount = receipt.receipt_items.reduce((sum, item) => {
        return sum + (Number(item.amount) || 0);
      }, 0);

      // console.log(`收據 ${receipt.receipt_number}: receipt.amount=${receipt.amount}, 項目總和=${totalAmount}`);

      return totalAmount;
    },

    // 獲取 Re-Print 收據的原始金額
    getOriginalReceiptAmount(receiptItem) {
      if (!receiptItem || !receiptItem.is_reprint) return '0.00';

      // 優先使用後端提供的原始收據金額
      if (receiptItem.reference_receipt_amount && typeof receiptItem.reference_receipt_amount !== 'undefined') {
        return Number(receiptItem.reference_receipt_amount).toFixed(2);
      }

      // 備用：使用 payment_step.step_amount
      if (receiptItem.payment_step && typeof receiptItem.payment_step.step_amount !== 'undefined') {
        return Number(receiptItem.payment_step.step_amount).toFixed(2);
      }

      return '0.00';
    },
  },
  mounted() {
    setTimeout(() => {
      var id = this.userData.id;
      var user_type_id = this.userData.user_type_id;
      if (user_type_id == 2) {
        this.invoiceOptions = this.invoiceOptions.filter(function(ele) {
          return ele.customer_id === id;
        });
        this.projectOptions = this.projectOptions.filter(function(ele) {
          return ele.customer_id === id;
        });
      }
    }, 1000);
  },
  setup() {
    const USER_APP_STORE_MODULE_NAME = "receipt";

    // Register module
    if (!store.hasModule(USER_APP_STORE_MODULE_NAME))
      store.registerModule(USER_APP_STORE_MODULE_NAME, receiptStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(USER_APP_STORE_MODULE_NAME))
        store.unregisterModule(USER_APP_STORE_MODULE_NAME);
    });



    const {
      fetchReceipts,
      tableColumns,
      perPage,
      currentPage,
      totalRow,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      receiptsList,
      refReceiptListTable,
      refetchData,
      ability,
      refetchOption,
      filter_customer_id,
      filter_payment_method_id,
      filter_project_id, 
      filter_invoice_id,
      filter_start_date,
      filter_end_date,
      filter_min_amount,
      filter_max_amount,
      invoiceOptions,
      projectOptions,
      customerOptions,
      paymentMethodOptions,
    } = useReceiptsList();

    // 新視窗編輯控制
    const openInNewWindow = ref(localStorage.getItem('receipt-edit-new-window') === 'true');

    // 監聽 checkbox 變化並保存到 localStorage
    watch(openInNewWindow, (newValue) => {
      localStorage.setItem('receipt-edit-new-window', newValue.toString());
    });

    return {
      fetchReceipts,
      tableColumns,
      perPage,
      currentPage,
      totalRow,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      receiptsList,
      refReceiptListTable,
      refetchData,
      ability,
      refetchOption,
      filter_project_id, 
      filter_invoice_id,
      filter_customer_id,
      filter_payment_method_id,
      filter_start_date,
      filter_end_date,
      filter_min_amount,
      filter_max_amount,
      invoiceOptions,
      projectOptions,
      customerOptions,
      paymentMethodOptions,
      openInNewWindow,
    };
  },
};
</script>
    
<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

// Re-Print 相關樣式
.badge-warning {
  background-color: #ffc107;
  color: #212529;
  font-size: 0.75em;
  padding: 0.25em 0.5em;
  border-radius: 0.25rem;
}

.text-warning {
  color: #856404 !important;
}

.reprint-amount {
  .font-weight-bold {
    font-weight: 600;
  }

  small {
    font-size: 0.8em;
    line-height: 1.2;
  }
}

// @media screen and (max-width: 768px) {

// }
// @media screen and (max-width: 575px) {

// }
</style>
    
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@/assets/scss/variables/_variables.scss";
</style>
    